import { useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { EnvelopeSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Two-step OTP recovery dialog — Iter15 Task 13: EMAIL ONLY (WhatsApp recovery removed).
 *   Step 1 — enter email → backend issues a 6-digit code (mocked: logged).
 *   Step 2 — enter code + new password → backend rotates the password.
 */
export default function RecoveryDialog({ open, onClose }) {
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setStep(1); setEmail(''); setCode(''); setNewPassword(''); setBusy(false);
  };

  const sendCode = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/auth/recovery/request`, { email });
      toast.success(t('otpSent'));
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const verifyAndSet = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/auth/recovery/verify`, { email, code, new_password: newPassword });
      toast.success(t('accountRecovered'));
      reset();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose?.(); } }}>
      <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-sm mx-auto" data-testid="recovery-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <EnvelopeSimple size={20} className="text-[#0ABAB5]" weight="fill" />
            {t('recoverAccount')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {step === 1 && (
            <>
              <p className="text-xs text-white/55">{t('emailRecoveryHint')}</p>
              <Input
                type="email"
                placeholder={t('email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 bg-[#050505] border-white/10 rounded-xl text-sm"
                data-testid="recovery-email"
              />
              <Button
                onClick={sendCode}
                disabled={busy || !email.trim() || !email.includes('@')}
                className="cta-button-orange w-full h-12 rounded-xl text-sm font-semibold"
                data-testid="recovery-send"
              >
                {busy ? '…' : t('sendCodeButton')}
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <p className="text-xs text-white/55">{t('enterOtp')}</p>
              <Input
                inputMode="numeric"
                pattern="\d*"
                maxLength={6}
                placeholder="••••••"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="h-12 bg-[#050505] border-white/10 rounded-xl text-center text-lg tracking-[0.5em] font-mono"
                data-testid="recovery-code"
              />
              <Input
                type="password"
                placeholder={t('newPassword')}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="h-12 bg-[#050505] border-white/10 rounded-xl text-sm"
                data-testid="recovery-newpass"
              />
              <Button
                onClick={verifyAndSet}
                disabled={busy || code.length !== 6 || newPassword.length < 6}
                className="cta-button-orange w-full h-12 rounded-xl text-sm font-semibold"
                data-testid="recovery-verify"
              >
                {busy ? '…' : t('verifyCodeButton')}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
